import React, { useCallback, useContext } from 'react';
import { useIntl } from 'react-intl-next';

import Form from '@atlaskit/form';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { ExperienceTrackerContext, AI_AUDIO_BRIEFING } from '@confluence/experience-tracker';

import { useAudioBriefing } from '../../../../hooks/useAudioBriefing';
import type { CustomLengthType, CustomToneType } from '../../../../types/customizationTypes';
import { CustomizePanelRestartButton } from '../../sub-components/RestartButton';
import { getSharedAttributes } from '../../../../utils/getSharedAttributes';

import { ToneSelector } from './ToneSelector';
import { DurationSelector } from './DurationSelector';

const styles = cssMap({
	grow: {
		flex: 1,
	},
	buttonWrapper: {
		paddingBlockStart: token('space.150'),
	},
});

interface CustomizationFormValues {
	length: CustomLengthType;
	tone: {
		value: CustomToneType;
	};
}

export const AudioBriefingPopupCustomizePanel = ({
	audioRef,
}: {
	audioRef: React.RefObject<HTMLAudioElement>;
}) => {
	const { locale } = useIntl();

	const [
		{
			activeContent,
			streamEnded,
			hasFetched,
			customization,
			isLoading,
			lastGeneratedBriefingConfig,
			additionalResources,
			playbackSpeed,
		},
		{ fetchBriefing },
	] = useAudioBriefing();

	const experienceTracker = useContext(ExperienceTrackerContext);

	const { trackAIInteractionInit, trackAIInteractionDismiss } = useAIEventsInstrumentation();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const sendAnalyticsUIEvent = useCallback(
		(
			actionSubjectId: string,
			data: CustomizationFormValues,
			additionalAttributes?: Record<string, number | string>,
		) => {
			const attributes = {
				audioLocale: locale,
				length: data.length,
				tone: data.tone.value,
				hasFetchedBriefing: hasFetched ?? false,
				...additionalAttributes,
			};
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'customizePanel',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId,
					attributes,
				},
			}).fire();
		},
		[createAnalyticsEvent, locale, hasFetched],
	);

	const handleStartNewBriefing = () => {
		if (audioRef?.current) {
			audioRef.current.pause();
		}

		if (!streamEnded) {
			experienceTracker.abort({
				name: AI_AUDIO_BRIEFING,
				reason: 'briefing customization was applied',
			});
			trackAIInteractionDismiss({
				attributes: getSharedAttributes({
					customization: lastGeneratedBriefingConfig?.customization,
					additionalResources: lastGeneratedBriefingConfig?.additionalResources,
					playbackSpeed,
					hasFetched,
				}),
			});
		}

		experienceTracker.start({
			name: AI_AUDIO_BRIEFING,
			attributes: {
				contentId: activeContent?.id,
			},
		});

		trackAIInteractionInit({
			attributes: getSharedAttributes({
				customization,
				additionalResources,
				playbackSpeed,
				hasFetched,
			}),
		});

		sendAnalyticsUIEvent('audioCustomize', {
			length: customization.length,
			tone: { value: customization.tone },
		});
		fetchBriefing();
	};

	const { customization: lastGeneratedCustomization } = lastGeneratedBriefingConfig || {};

	return (
		<Box xcss={styles.grow}>
			<Form<CustomizationFormValues> onSubmit={handleStartNewBriefing}>
				{({ formProps }) => (
					<form {...formProps}>
						<Stack space="space.100">
							<ToneSelector />
							<DurationSelector />
							{hasFetched && (
								<Box xcss={styles.buttonWrapper}>
									<CustomizePanelRestartButton
										fetchBriefing={handleStartNewBriefing}
										currentCustomization={customization}
										isLoading={isLoading}
										lastGeneratedCustomization={lastGeneratedCustomization}
									/>
								</Box>
							)}
						</Stack>
					</form>
				)}
			</Form>
		</Box>
	);
};
